import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from '~/utils/navigation';

import { selectLimitsModalData, updateLimitsModal } from '~/components/account/ducks';
import { selectPaymentPixelData } from '~/components/payment/ducks/quick-deposit-duck';
import Icon, { ICON_CLOSE } from '~/components/icon';
import { MainTitle } from '~/components/misc/section-head';
import useTranslations from '~/components/translation/use-translations';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';
import usePaymentTracking from '~/components/payment-beta/deposit/hooks/use-payment-tracking';

import { textKeys, getLimitsContent, getLimitPopupTracking } from './constants';
import { LimitType, LIMIT_POPUP_ACTIONS } from '~/components/limits/types';
import {
	ButtonOutlinedCustom,
	ButtonContainer,
	CloseButton,
	CloseIconWrapper,
	LimitContainer,
	SubTitle
} from './styled';

const RGLimit = ({ qdLimitsFlow }: { qdLimitsFlow?: boolean }) => {
	const dispatch = useDispatch();
	const router = useRouter();

	const { trackEvent } = usePaymentTracking();
	const { translations: t } = useTranslations(textKeys);
	const { responsibleGamingLimitType } = useSelector(selectLimitsModalData) ?? {};
	const paymentPixelData = useSelector(selectPaymentPixelData);
	const transformPath = useContextualPathTransformer();

	// Determine the screen value based on flow
	const getScreenValue = useCallback(() => {
		if (qdLimitsFlow && paymentPixelData?.pixel_screen) {
			return paymentPixelData.pixel_screen;
		}
		return 'account-page';
	}, [qdLimitsFlow, paymentPixelData]);

	const limitContent = useMemo(() => getLimitsContent(responsibleGamingLimitType), [responsibleGamingLimitType]);
	const hasTracked = useRef(false);

	useEffect(() => {
		if (!hasTracked.current && responsibleGamingLimitType) {
			trackEvent({
				...getLimitPopupTracking(responsibleGamingLimitType as LimitType, LIMIT_POPUP_ACTIONS.VIEW),
				pixel_screen: getScreenValue()
			});
			hasTracked.current = true;
		}
	}, [responsibleGamingLimitType, trackEvent, getScreenValue]);

	const onClose = useCallback(
		(informMobileApps = true) => {
			dispatch(updateLimitsModal(false, {}, informMobileApps));
		},
		[dispatch]
	);

	const onClickClose = useCallback(() => {
		trackEvent({
			...getLimitPopupTracking(responsibleGamingLimitType as LimitType, LIMIT_POPUP_ACTIONS.CLOSE),
			pixel_screen: getScreenValue()
		});
		onClose();
	}, [onClose, responsibleGamingLimitType, trackEvent, getScreenValue]);

	const onClick = useCallback(() => {
		trackEvent({
			...getLimitPopupTracking(responsibleGamingLimitType as LimitType, LIMIT_POPUP_ACTIONS.CTA_CLICK),
			pixel_screen: getScreenValue()
		});
		router.push(transformPath(limitContent.navigateUrl));
		onClose(false);
	}, [
		trackEvent,
		responsibleGamingLimitType,
		router,
		transformPath,
		limitContent.navigateUrl,
		onClose,
		getScreenValue
	]);

	return (
		<LimitContainer data-at="spending-screen">
			<MainTitle padding="10px 18px">{t[limitContent.title]}</MainTitle>
			<SubTitle>{t[limitContent.subTitle]}</SubTitle>
			<CloseButton onClick={onClickClose}>
				<CloseIconWrapper>
					<Icon type={ICON_CLOSE} />
				</CloseIconWrapper>
			</CloseButton>
			<ButtonContainer>
				<ButtonOutlinedCustom data-at="spending-limit-modal" onClick={onClick} $customFont="var(--h3)">
					{t[limitContent.buttonText]}
				</ButtonOutlinedCustom>
			</ButtonContainer>
		</LimitContainer>
	);
};

export default RGLimit;
